import React, { useState } from "react";
import { fmtKc, fmtPct } from "../lib/format";

/**
 * Grafy jsou ručně kreslené SVG — žádná knihovna navíc.
 *
 * Barvy nejsou náhodné: tržby zelená (#17876a), náklady oranžová (#c8551a).
 * Dvojice prošla kontrolou na barvosleposti (ΔE 10,3 protan) i na kontrast
 * vůči bílé kartě, takže sloupce od sebe pozná i protanop.
 */
export const BARVA = {
  trzby: "#17876a",
  naklady: "#c8551a",
  marze: "#14161a",
  po_terminu: "#d93a2b",
  do_14: "#c8551a",
  pozdeji: "#8b8f96",
  mrizka: "#eeece7",
  osa: "#a0a4aa",
};

const W = 720;
const H = 260;
const PAD = { top: 16, right: 34, bottom: 30, left: 62 };
const PW = W - PAD.left - PAD.right;
const PH = H - PAD.top - PAD.bottom;

/** Sloupec se zaoblenou horní hranou (4 px), spodek sedí na základně. */
function sloupec(x, y, w, h, r = 4) {
  if (h <= 0) return "";
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h} L${x},${y + rr} Q${x},${y} ${x + rr},${y} `
    + `L${x + w - rr},${y} Q${x + w},${y} ${x + w},${y + rr} L${x + w},${y + h} Z`;
}

/** Osa má končit na kulatém čísle — „990 tis." nikdo v hlavě neporovnává. */
function kulateMax(v) {
  if (v <= 0) return 1;
  const rad = Math.pow(10, Math.floor(Math.log10(v)));
  const podil = v / rad;
  const krok = podil <= 1 ? 1 : podil <= 2 ? 2 : podil <= 2.5 ? 2.5 : podil <= 5 ? 5 : 10;
  return krok * rad;
}


function tisicKc(v) {
  const a = Math.abs(v);
  if (a >= 1000000) return `${Math.round(v / 100000) / 10} mil.`;
  if (a >= 1000) return `${Math.round(v / 1000)} tis.`;
  return String(Math.round(v));
}

function Legenda({ polozky }) {
  return (
    <div className="flex items-center gap-4 flex-wrap mb-2">
      {polozky.map((p) => (
        <span key={p.label} className="flex items-center gap-1.5 text-[11.5px] text-sec-3">
          <span className="h-2.5 w-2.5 rounded-[3px] shrink-0" style={{ background: p.barva }} />
          {p.label}
        </span>
      ))}
    </div>
  );
}

/** Bublina s hodnotami u kurzoru. Drží se uvnitř plátna, ať nevyjede z karty. */
function Bublina({ x, y, radky, nadpis }) {
  const sirka = 168;
  const vyska = 22 + radky.length * 16;
  const px = Math.min(Math.max(x - sirka / 2, 2), W - sirka - 2);
  const py = Math.max(y - vyska - 10, 2);
  return (
    <g pointerEvents="none">
      <rect x={px} y={py} width={sirka} height={vyska} rx="10" fill="#14161a" opacity="0.96" />
      <text x={px + 10} y={py + 16} fill="#ffffff" fontSize="11" fontWeight="700">{nadpis}</text>
      {radky.map((r, i) => (
        <g key={r.label}>
          {r.barva && <rect x={px + 10} y={py + 26 + i * 16 - 7} width="7" height="7" rx="2" fill={r.barva} />}
          <text x={px + (r.barva ? 22 : 10)} y={py + 26 + i * 16} fill="#c9ced5" fontSize="10.5">{r.label}</text>
          <text x={px + sirka - 10} y={py + 26 + i * 16} fill="#ffffff" fontSize="10.5"
            fontWeight="600" textAnchor="end">{r.hodnota}</text>
        </g>
      ))}
    </g>
  );
}

/** Tržby vs. náklady po měsících. Obojí bez DPH, aby se to dalo porovnat. */
export function SloupceMesice({ data }) {
  const [aktivni, setAktivni] = useState(null);
  const [tabulka, setTabulka] = useState(false);
  const max = kulateMax(Math.max(1, ...data.map((d) => Math.max(d.trzby, d.naklady))));
  const skala = (v) => PH - (v / max) * PH;
  const slot = PW / Math.max(data.length, 1);
  const sirka = Math.min((slot - 8) / 2, 22);

  if (tabulka) {
    return (
      <>
        <PrepinacTabulky tabulka={tabulka} onZmena={setTabulka} />
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="graf-mesice-tabulka">
            <thead>
              <tr className="label-up text-muted-2 border-b border-line-soft">
                <th className="text-left font-600 py-2">Měsíc</th>
                <th className="text-right font-600 py-2">Tržby</th>
                <th className="text-right font-600 py-2">Náklady</th>
                <th className="text-right font-600 py-2">Marže</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.mesic} className="border-b border-line-softer last:border-0">
                  <td className="py-1.5 text-sec-3">{d.label}</td>
                  <td className="py-1.5 text-right amount">{fmtKc(d.trzby)} Kč</td>
                  <td className="py-1.5 text-right amount">{fmtKc(d.naklady)} Kč</td>
                  <td className="py-1.5 text-right amount font-700">{fmtKc(d.marze)} Kč</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-start justify-between">
        <Legenda polozky={[{ label: "Tržby bez DPH", barva: BARVA.trzby },
          { label: "Náklady bez DPH", barva: BARVA.naklady }]} />
        <PrepinacTabulky tabulka={tabulka} onZmena={setTabulka} />
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="graf-mesice"
        onMouseLeave={() => setAktivni(null)}>
        {[0, 0.25, 0.5, 0.75, 1].map((p) => (
          <g key={p}>
            <line x1={PAD.left} x2={W - PAD.right} y1={PAD.top + PH * p} y2={PAD.top + PH * p}
              stroke={BARVA.mrizka} strokeWidth="1" />
            <text x={PAD.left - 8} y={PAD.top + PH * p + 3.5} textAnchor="end" fontSize="10"
              fill={BARVA.osa} fontFamily="Switzer, system-ui, sans-serif">{tisicKc(max * (1 - p))}</text>
          </g>
        ))}
        {data.map((d, i) => {
          const x0 = PAD.left + i * slot;
          const stred = x0 + slot / 2;
          return (
            <g key={d.mesic}>
              <path d={sloupec(stred - sirka - 1, PAD.top + skala(d.trzby), sirka, PH - skala(d.trzby))}
                fill={BARVA.trzby} />
              <path d={sloupec(stred + 1, PAD.top + skala(d.naklady), sirka, PH - skala(d.naklady))}
                fill={BARVA.naklady} />
              <text x={stred} y={H - 10} textAnchor="middle" fontSize="10" fill={BARVA.osa}
                fontFamily="Switzer, system-ui, sans-serif">{d.label}</text>
              <rect x={x0} y={PAD.top} width={slot} height={PH} fill="transparent"
                onMouseEnter={() => setAktivni(i)} />
              {aktivni === i && <rect x={x0} y={PAD.top} width={slot} height={PH} fill="#14161a" opacity="0.04" />}
            </g>
          );
        })}
        {aktivni !== null && data[aktivni] && (
          <Bublina x={PAD.left + aktivni * slot + slot / 2} y={PAD.top + 40} nadpis={data[aktivni].label}
            radky={[
              { label: "Tržby", hodnota: `${fmtKc(data[aktivni].trzby)} Kč`, barva: BARVA.trzby },
              { label: "Náklady", hodnota: `${fmtKc(data[aktivni].naklady)} Kč`, barva: BARVA.naklady },
              { label: "Marže", hodnota: `${fmtKc(data[aktivni].marze)} Kč` },
            ]} />
        )}
      </svg>
    </>
  );
}

function PrepinacTabulky({ tabulka, onZmena }) {
  return (
    <button onClick={() => onZmena(!tabulka)} data-testid="prepinac-tabulky"
      className="text-[11.5px] font-700 text-accent-hover shrink-0">
      {tabulka ? "Zpět na graf" : "Tabulka"}
    </button>
  );
}

/** Marže v procentech. Vlastní graf, ne druhá osa u korun. */
export function CaraMarze({ data }) {
  const [aktivni, setAktivni] = useState(null);
  const body = data.filter((d) => d.marze_pct !== null && d.marze_pct !== undefined);
  if (body.length < 2) {
    return <p className="text-[12.5px] text-muted py-6 text-center">Na křivku marže zatím není dost měsíců.</p>;
  }
  const hodnoty = body.map((d) => d.marze_pct);
  // procenta po desítkách, ať se dá odečítat i bez počítání
  const max = Math.ceil(Math.max(10, ...hodnoty) / 10) * 10;
  const min = Math.floor(Math.min(0, ...hodnoty) / 10) * 10;
  const skala = (v) => PH - ((v - min) / (max - min || 1)) * PH;
  const krok = PW / Math.max(body.length - 1, 1);
  const x = (i) => PAD.left + i * krok;
  const cesta = body.map((d, i) => `${i ? "L" : "M"}${x(i)},${PAD.top + skala(d.marze_pct)}`).join(" ");
  const nula = PAD.top + skala(0);

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="graf-marze"
        onMouseLeave={() => setAktivni(null)}>
        {[0, 0.5, 1].map((p) => (
          <g key={p}>
            <line x1={PAD.left} x2={W - PAD.right} y1={PAD.top + PH * p} y2={PAD.top + PH * p}
              stroke={BARVA.mrizka} strokeWidth="1" />
            <text x={PAD.left - 8} y={PAD.top + PH * p + 3.5} textAnchor="end" fontSize="10"
              fill={BARVA.osa} fontFamily="Switzer, system-ui, sans-serif">
              {Math.round(max - (max - min) * p)} %
            </text>
          </g>
        ))}
        {min < 0 && <line x1={PAD.left} x2={W - PAD.right} y1={nula} y2={nula} stroke={BARVA.osa} strokeWidth="1" />}
        <path d={cesta} fill="none" stroke={BARVA.marze} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {body.map((d, i) => (
          <g key={d.mesic}>
            <text x={x(i)} y={H - 10} textAnchor="middle" fontSize="10" fill={BARVA.osa}
              fontFamily="Switzer, system-ui, sans-serif">{d.label}</text>
            {aktivni === i && (
              <>
                <line x1={x(i)} x2={x(i)} y1={PAD.top} y2={PAD.top + PH} stroke={BARVA.mrizka} strokeWidth="1" />
                <circle cx={x(i)} cy={PAD.top + skala(d.marze_pct)} r="5" fill={BARVA.marze}
                  stroke="#ffffff" strokeWidth="2" />
              </>
            )}
            <rect x={x(i) - krok / 2} y={PAD.top} width={krok} height={PH} fill="transparent"
              onMouseEnter={() => setAktivni(i)} />
          </g>
        ))}
        {/* popisek jen u posledního bodu — číslo u každého by graf zaplevelilo */}
        <text x={x(body.length - 1)} y={PAD.top + skala(body[body.length - 1].marze_pct) - 10}
          textAnchor="end" fontSize="11" fontWeight="700" fill={BARVA.marze}
          fontFamily="Switzer, system-ui, sans-serif">
          {fmtPct(body[body.length - 1].marze_pct)} %
        </text>
        {aktivni !== null && body[aktivni] && (
          <Bublina x={x(aktivni)} y={PAD.top + skala(body[aktivni].marze_pct)} nadpis={body[aktivni].label}
            radky={[
              { label: "Marže", hodnota: `${fmtPct(body[aktivni].marze_pct)} %` },
              { label: "V korunách", hodnota: `${fmtKc(body[aktivni].marze)} Kč` },
            ]} />
        )}
      </svg>
    </>
  );
}

/** Vodorovné pruhy — dodavatelé, koše splatnosti, marže zakázek. */
export function Pruhy({ polozky, barva, zaporna, onKlik, prazdno = "Zatím není z čeho počítat." }) {
  if (!polozky.length) return <p className="text-[12.5px] text-muted py-4">{prazdno}</p>;
  // Měřítko je společné pro celý seznam — jinak by každý pruh vyšel na plnou
  // šířku a graf by nic neříkal.
  const max = Math.max(1, ...polozky.map((p) => Math.abs(p.hodnota)));
  const Radek = onKlik ? "button" : "div";
  return (
    <div className="space-y-2.5" data-testid="graf-pruhy">
      {polozky.map((p) => (
        <Radek key={p.label} onClick={onKlik ? () => onKlik(p) : undefined}
          data-testid={p.id ? `pruh-${p.id}` : undefined}
          className={`flex items-center gap-3 w-full text-left ${onKlik ? "hover:opacity-80" : ""}`}>
          <span className="w-[42%] min-w-0 truncate text-[12.5px] text-ink" title={p.label}>
            {p.label}
            {p.popis && <span className="block text-[10.5px] text-muted truncate">{p.popis}</span>}
          </span>
          <span className="flex-1 h-2.5 bg-line-soft rounded-[4px] overflow-hidden">
            <span className="block h-full rounded-[4px]"
              style={{
                width: `${Math.max((Math.abs(p.hodnota) / max) * 100, 2)}%`,
                background: p.barva || (zaporna && p.hodnota < 0 ? BARVA.po_terminu : barva || BARVA.naklady),
              }} />
          </span>
          <span className={`amount text-[12.5px] font-700 w-[110px] text-right shrink-0 ${
            zaporna && p.hodnota < 0 ? "text-danger" : "text-ink"}`}>
            {fmtKc(p.hodnota)} Kč
          </span>
        </Radek>
      ))}
    </div>
  );
}

/** Dva díly jednoho celku (materiál ze skladu vs. přímý nákup). */
export function Podil({ casti }) {
  const celkem = casti.reduce((s, c) => s + Math.max(c.hodnota, 0), 0);
  if (!celkem) return <p className="text-[12.5px] text-muted py-4">Zatím žádný materiál na zakázkách.</p>;
  return (
    <div data-testid="graf-podil">
      <div className="flex gap-[2px] h-3 mb-3">
        {casti.map((c) => (
          <span key={c.label} className="h-full first:rounded-l-[4px] last:rounded-r-[4px]"
            style={{ width: `${(Math.max(c.hodnota, 0) / celkem) * 100}%`, background: c.barva }} />
        ))}
      </div>
      <div className="space-y-1.5">
        {casti.map((c) => (
          <div key={c.label} className="flex items-center gap-2 text-[12.5px]">
            <span className="h-2.5 w-2.5 rounded-[3px] shrink-0" style={{ background: c.barva }} />
            <span className="text-sec-3">{c.label}</span>
            <span className="ml-auto amount font-700 text-ink">{fmtKc(c.hodnota)} Kč</span>
            <span className="amount text-muted w-[46px] text-right">
              {Math.round((Math.max(c.hodnota, 0) / celkem) * 100)} %
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
